/**
 * Quotes List
 *
 * Renders the "I miei preventivi" page (/account?view=quotes).
 * Fetches the customer's saved quotes from b-config and wires up the
 * Open / Archive actions. Pure DOM, no framework dependency.
 *
 * Auth payload (HMAC + customer_id) comes from #bconfig-auth, mirroring the
 * pattern used in quote-manager.js.
 */
(function () {
  'use strict';

  function getAuth() {
    const el = document.getElementById('bconfig-auth');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (_) { return null; }
  }

  function getOptions() {
    const c = document.getElementById('my-quotes-container');
    if (!c) return null;
    return {
      apiBase: (c.getAttribute('data-bconfig-api-base') || '').replace(/\/+$/, ''),
      useAppProxy: c.getAttribute('data-bconfig-use-proxy') === 'true'
    };
  }

  /**
   * Read a localized UI string from the `data-i18n-<key>` attribute on the
   * #my-quotes-container element. Liquid (sections/main-quotes.liquid) renders
   * those values via `{{ 'quote.X' | t }}` against the active storefront
   * locale, so by the time JS runs they're already in the visitor's language.
   * Falls back to `defaultText` if the attribute is missing — so themes that
   * have not yet pushed the i18n locales degrade to italian instead of crashing.
   */
  function getUiText(key, defaultText) {
    const c = document.getElementById('my-quotes-container');
    if (!c) return defaultText;
    const attr = 'i18n' + key.replace(/(^|-)([a-z])/g, (_, _dash, ch) => ch.toUpperCase());
    const v = c.dataset[attr];
    return (v && v.length > 0) ? v : defaultText;
  }

  function endpoint(suffix) {
    const o = getOptions();
    if (!o) return null;
    const path = '/api/quotes' + (suffix || '');
    return o.useAppProxy ? '/apps/quotes' + path : o.apiBase + path;
  }

  function authHeaders() {
    const o = getOptions();
    if (!o || o.useAppProxy) return {};
    const a = getAuth() || {};
    return {
      'X-Shopify-Customer-Id': String(a.customer_id || ''),
      'X-Shopify-Timestamp': String(a.timestamp || ''),
      'X-Shopify-Hmac': String(a.hmac || ''),
      'X-Shopify-Shop-Domain': String(a.shop_domain || '')
    };
  }

  function fmtDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
  }

  function fmtMoney(cents, currency) {
    if (typeof cents !== 'number') return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + ' ' + (currency || 'EUR');
    }
  }

  function statusBadge(status) {
    const span = document.createElement('span');
    const s = status || 'draft';
    span.className = 'my-quotes__status my-quotes__status--' + s;
    // Localize the badge text: data-i18n-status-{saved|draft|archived} on the
    // container is the source of truth; unknown statuses fall back to the raw
    // value so we don't blank the cell on schema additions.
    span.textContent = getUiText('status-' + s, s);
    return span;
  }

  function renderRows(quotes) {
    const tbody = document.getElementById('my-quotes-tbody');
    const table = document.getElementById('my-quotes-table');
    const empty = document.getElementById('my-quotes-empty');
    const state = document.getElementById('my-quotes-state');
    if (!tbody || !table || !empty || !state) return;

    tbody.textContent = '';
    state.style.display = 'none';

    if (!quotes.length) {
      empty.hidden = false;
      table.hidden = true;
      return;
    }

    empty.hidden = true;
    table.hidden = false;

    quotes.forEach(q => {
      const tr = document.createElement('tr');
      tr.dataset.quoteId = q.id;

      const tdNumber = document.createElement('td');
      tdNumber.className = 'my-quotes__number';
      tdNumber.textContent = q.number || '—';

      const tdDate = document.createElement('td');
      tdDate.textContent = fmtDate(q.created_at);

      const tdHeader = document.createElement('td');
      const intest = (q.header && (q.header.intestazione_cliente || q.header.contatto_riferimento)) || '—';
      tdHeader.textContent = intest;

      const tdStatus = document.createElement('td');
      tdStatus.appendChild(statusBadge(q.status));

      const tdTotal = document.createElement('td');
      const cents = q.totals && typeof q.totals.total === 'number' ? q.totals.total : null;
      tdTotal.textContent = cents != null ? fmtMoney(cents, q.totals && q.totals.currency) : '—';

      const tdActions = document.createElement('td');
      const openBtn = document.createElement('a');
      openBtn.className = 'my-quotes__action';
      // Cart page reads ?quote_id and the quote-manager.js loadQuoteFromServer takes over
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      openBtn.href = root.replace(/\/$/, '') + '/cart?quote_id=' + encodeURIComponent(q.id);
      openBtn.textContent = getUiText('action-open', 'Apri');
      tdActions.appendChild(openBtn);

      const cloneBtn = document.createElement('button');
      cloneBtn.type = 'button';
      cloneBtn.className = 'my-quotes__action';
      cloneBtn.textContent = getUiText('action-clone', 'Clona');
      cloneBtn.addEventListener('click', () => clone(q.id, cloneBtn));
      tdActions.appendChild(cloneBtn);

      const archiveBtn = document.createElement('button');
      archiveBtn.type = 'button';
      archiveBtn.className = 'my-quotes__action my-quotes__action--danger';
      archiveBtn.textContent = getUiText('action-archive', 'Archivia');
      archiveBtn.addEventListener('click', () => archive(q.id));
      tdActions.appendChild(archiveBtn);

      tr.appendChild(tdNumber);
      tr.appendChild(tdDate);
      tr.appendChild(tdHeader);
      tr.appendChild(tdStatus);
      tr.appendChild(tdTotal);
      tr.appendChild(tdActions);

      tbody.appendChild(tr);
    });
  }

  async function load() {
    const url = endpoint();
    if (!url) return;
    const state = document.getElementById('my-quotes-state');
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        if (state) {
          // Locale templates use {status} as placeholder for res.status — we
          // do a literal replace because Liquid pre-rendered the localized
          // string for us; ICU/MessageFormat is overkill for a single token.
          const tpl = getUiText('error-fetch-status', 'Errore ({status}) nel recupero dei preventivi.');
          state.textContent = tpl.replace('{status}', String(res.status));
        }
        return;
      }
      const data = await res.json();
      renderRows(Array.isArray(data && data.quotes) ? data.quotes : []);
    } catch (err) {
      console.error('[QuotesList] fetch failed', err);
      if (state) state.textContent = getUiText('error-fetch-network', 'Errore di rete nel recupero dei preventivi.');
    }
  }

  /**
   * Clone a quote: GET the full source (the list payload is slim — no items),
   * then POST it back WITHOUT an id so b-config creates a new record. The new
   * record's "intestazione cliente" is prefixed (localized "Copia di "). We do
   * NOT open it — per spec the copy just appears in the list. The POST body
   * mirrors buildQuotePayload() in quote-manager.js so the API accepts it.
   */
  async function clone(id, btn) {
    const prev = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    try {
      const res = await fetch(endpoint('/' + id), { headers: authHeaders() });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const src = (await res.json()).quote;
      if (!src) throw new Error('quote not found');

      const prefix = getUiText('clone-prefix', 'Copia di ');
      const header = Object.assign({}, src.header || {});
      header.intestazione_cliente = prefix + (header.intestazione_cliente || '');

      const payload = {
        customer_email: src.customer_email || '',
        status: 'saved',
        is_test: !!src.is_test,
        header: header,
        items: Array.isArray(src.items) ? src.items : [],
        totals: src.totals || {}
      };

      const post = await fetch(endpoint(), {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify(payload)
      });
      if (!post.ok) throw new Error('HTTP ' + post.status);

      // Reload the whole list — the copy appears (sorted by created_at). Simpler
      // and safer than splicing a row; load() re-renders tbody from scratch so
      // the disabled btn reference is discarded on success.
      await load();
    } catch (err) {
      console.error('[QuotesList] clone failed', err);
      alert(getUiText('error-clone', 'Errore clonazione: ') + (err && err.message || err));
      if (btn) { btn.disabled = false; btn.textContent = prev; }
    }
  }

  async function archive(id) {
    if (!confirm(getUiText('confirm-archive', 'Archiviare questo preventivo?'))) return;
    try {
      const res = await fetch(endpoint('/' + id), { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      // Remove the row optimistically
      const row = document.querySelector(`tr[data-quote-id="${CSS.escape(id)}"]`);
      if (row) row.remove();
      const tbody = document.getElementById('my-quotes-tbody');
      if (tbody && !tbody.children.length) {
        const empty = document.getElementById('my-quotes-empty');
        const table = document.getElementById('my-quotes-table');
        if (empty) empty.hidden = false;
        if (table) table.hidden = true;
      }
    } catch (err) {
      alert(getUiText('error-archive', 'Errore archiviazione: ') + (err && err.message || err));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', load);
  } else {
    load();
  }
})();
